/**
 * /downhole/work-orders/:workOrderId — one work order on its own page
 * (fieldiq-api#882 `GET /work-orders/:id`). This was a panel beside the Work
 * Orders list; it moved here when split panes were banned.
 *
 * THE OFFICE'S WRITES (api#905 `PATCH /work-orders/:id`), for admin,
 * office_staff and dispatcher (lib/downhole-roles.ts):
 *   Assign      `assigned_to`, in the body. A teardown opens its work orders
 *               UNASSIGNED, and the field shell's My work lists only orders
 *               assigned to the hand, so without this an order a teardown
 *               opened reached nobody.
 *   Start / Waiting on parts / Resume / Complete / Cancel / Edit — on the band,
 *               along the API's transition map, so no button is refused for
 *               its status. Complete is the field shell's dialog (pass / fail
 *               for an inspection type); Cancel and Complete can return the
 *               tool to stock, behind dope & cleaning (WorkOrderDialogs).
 * A completed or cancelled order is final on the API and offers no control.
 *
 * THE PARTS LIST is the API's `parts_list`, each line with its quantity and
 * UNIT cost. The totals are the API's `parts_cost` and `total_cost`, shown as
 * they are, and no line is extended. `findings` and `resolution` are different
 * facts and are shown under their own names. `assigned_to` is a user id; the
 * name is printed.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, CheckCircle2, Loader2, PackageSearch, Pencil, Play } from 'lucide-react';
import { getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeWorkOrder, useUpdateWorkOrder } from '../../hooks/useDownhole';
import { formatInstant, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtHours, fmtQty } from '../../lib/display-labels';
import { DH_CATEGORY_LABEL, DH_PRIORITY_LABEL, DH_WORK_ORDER_STATUS_SKIN, DH_WORK_TYPE_LABEL, dhLabel } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { canAssignDownholeWork, canManageDownholeWorkOrders } from '../../lib/downhole-roles';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_WORK_ORDER_TRANSITIONS, type DhWorkOrder } from '../../types/downhole';
import { DH_BAND_SECONDARY, DH_INPUT, DhBadge, DhEmpty, DhPanel, DhSpec } from './DownholeBits';
import { CancelWorkOrderDialog, EditWorkOrderDialog, personName, useDownholeAssignees } from './WorkOrderDialogs';
import { CompleteWorkOrderDialog } from './field/FieldWorkOrderScreen';
import { useBackToList } from './recordPage';

const LIST = '/downhole/work-orders';

const isLive = (wo: DhWorkOrder) => wo.status !== 'completed' && wo.status !== 'cancelled';

/**
 * Who works this order. The options are the tenant's active people (customers
 * excluded) PLUS the current assignee, so an assignee who has since left still
 * shows as who it is rather than as the first option (a select cannot show a
 * value it has no option for). Saved on change; the list is read again.
 */
function AssignControl({ wo }: { wo: DhWorkOrder }) {
  const update = useUpdateWorkOrder();
  const people = useDownholeAssignees();
  const [value, setValue] = useState(wo.assigned_to ?? '');
  React.useEffect(() => setValue(wo.assigned_to ?? ''), [wo.assigned_to]);
  const list = people.people;
  const known = !wo.assigned_to || list.some((u) => u.id === wo.assigned_to);

  const save = async (next: string) => {
    const before = value;
    setValue(next);
    if (!onlineOrSay(toast.error)) {
      setValue(before);
      return;
    }
    try {
      await update.mutateAsync({ id: wo.id, body: { assigned_to: next || null } });
      const who = next ? personName(list.find((u) => u.id === next) ?? { id: next }) : null;
      toast.success(who ? `${wo.wo_number ?? 'Work order'} assigned to ${who}` : `${wo.wo_number ?? 'Work order'} unassigned`);
    } catch (error) {
      setValue(before);
      toast.error(getApiErrorMessage(error, 'The work order was not assigned.'));
    }
  };

  return (
    <div>
      <label htmlFor={`assign-${wo.id}`} className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
        Assigned to
      </label>
      <select
        id={`assign-${wo.id}`}
        className={DH_INPUT}
        value={value}
        disabled={update.isPending || !people.data}
        onChange={(e) => save(e.target.value)}
        aria-describedby={`assign-hint-${wo.id}`}
      >
        <option value="">Not assigned</option>
        {!known && <option value={wo.assigned_to ?? ''}>{wo.assigned_to_name || 'Current assignee'}</option>}
        {list.map((u) => (
          <option key={u.id} value={u.id}>
            {personName(u)}
          </option>
        ))}
      </select>
      <p id={`assign-hint-${wo.id}`} className="mt-1 text-xs text-fieldiq-muted">
        {people.data ? 'It shows in their My work on the field app.' : 'Loading people…'}
      </p>
    </div>
  );
}

/**
 * The office's status and edit controls, on the band. Each move button is on
 * screen only where the transition map allows it; Complete and Cancel open
 * their dialogs. Dialogs are keyed on the order.
 */
function WorkOrderActions({ wo, currency }: { wo: DhWorkOrder; currency: string | null }) {
  const update = useUpdateWorkOrder();
  const [dialog, setDialog] = useState<'complete' | 'cancel' | 'edit' | null>(null);
  const allowed = DH_WORK_ORDER_TRANSITIONS[wo.status] ?? [];

  const move = async (status: string, label: string) => {
    if (!onlineOrSay(toast.error)) return;
    try {
      await update.mutateAsync({ id: wo.id, body: { status } });
      toast.success(`${wo.wo_number ?? 'Work order'} ${label}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The work order was not updated.'));
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {allowed.includes('in_progress') && (
        <button type="button" className={DH_BAND_SECONDARY} disabled={update.isPending} onClick={() => move('in_progress', wo.status === 'awaiting_parts' ? 'resumed' : 'started')}>
          <Play className="size-4" aria-hidden="true" />
          {wo.status === 'awaiting_parts' ? 'Resume' : 'Start'}
        </button>
      )}
      {allowed.includes('awaiting_parts') && (
        <button type="button" className={DH_BAND_SECONDARY} disabled={update.isPending} onClick={() => move('awaiting_parts', 'marked waiting on parts')}>
          <PackageSearch className="size-4" aria-hidden="true" />
          Waiting on parts
        </button>
      )}
      <button type="button" className={DH_BAND_SECONDARY} onClick={() => setDialog('edit')}>
        <Pencil className="size-4" aria-hidden="true" />
        Edit
      </button>
      {allowed.includes('cancelled') && (
        <button type="button" className={DH_BAND_SECONDARY} disabled={update.isPending} onClick={() => setDialog('cancel')}>
          <Ban className="size-4" aria-hidden="true" />
          Cancel order
        </button>
      )}
      {allowed.includes('completed') && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={update.isPending} onClick={() => setDialog('complete')}>
          <CheckCircle2 className="size-4" aria-hidden="true" />
          Complete
        </button>
      )}
      {dialog === 'complete' && <CompleteWorkOrderDialog key={wo.id} open onClose={() => setDialog(null)} wo={wo} />}
      {dialog === 'cancel' && <CancelWorkOrderDialog key={wo.id} open onClose={() => setDialog(null)} wo={wo} />}
      {dialog === 'edit' && <EditWorkOrderDialog key={wo.id} open onClose={() => setDialog(null)} wo={wo} currency={currency} />}
    </div>
  );
}

/** The parts as the API stores them: quantity and unit cost. The total is the API's `parts_cost`, above. */
function PartsList({ wo, currency }: { wo: DhWorkOrder; currency: string | null }) {
  const parts = wo.parts_list ?? [];
  return (
    <div className="border-t border-fieldiq-border px-4 py-3">
      <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Parts list</p>
      {parts.length === 0 ? (
        <p className="mt-1 text-sm text-fieldiq-text">No parts on this order.</p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead className="text-left text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr>
              <th scope="col" className="py-1 pr-3">Part</th>
              <th scope="col" className="py-1 pr-3 text-right">Qty</th>
              <th scope="col" className="py-1 text-right">Unit cost</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p, i) => (
              <tr key={i} className="border-t border-fieldiq-border">
                <td className="py-1.5 pr-3 text-fieldiq-text">{p.part || '—'}</td>
                <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-fieldiq-text">{fmtQty(p.qty)}</td>
                <td className="py-1.5 text-right font-mono tabular-nums text-fieldiq-text">{fmtCurrency(p.cost, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WorkOrderDetail({ wo, tz, currency }: { wo: DhWorkOrder; tz: string | null; currency: string | null }) {
  const { user } = useAuth();
  const canAssign = canAssignDownholeWork(user?.role) && isLive(wo);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={wo.status} />
        <span className="text-sm text-fieldiq-text">{dhLabel(DH_WORK_TYPE_LABEL, wo.work_type)}</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 lg:grid-cols-4">
        <DhSpec
          label="Component"
          value={
            wo.component_id ? (
              <Link to={`/downhole/components/${wo.component_id}`} className="font-mono text-fieldiq-accent-signal hover:underline">
                {wo.component_serial || '—'}
              </Link>
            ) : (
              '—'
            )
          }
        />
        <DhSpec label="Category" value={dhLabel(DH_CATEGORY_LABEL, wo.component_category)} />
        <DhSpec label="Priority" value={dhLabel(DH_PRIORITY_LABEL, wo.priority)} />
        {canAssign ? (
          <div className="col-span-2">
            <AssignControl wo={wo} />
          </div>
        ) : (
          <DhSpec label="Assigned to" value={wo.assigned_to ? displayValue(wo.assigned_to_name) : 'Not assigned'} />
        )}
        <DhSpec label="Opened" value={formatInstant(wo.created_at, tz) ?? '—'} />
        {/* due_date is a calendar day ('YYYY-MM-DD'), not an instant. */}
        <DhSpec label="Due" value={formatInstantTenantDay(wo.due_date, tz) ?? '—'} />
        <DhSpec label="Started" value={formatInstant(wo.started_at, tz) ?? '—'} />
        <DhSpec label="Completed" value={formatInstant(wo.completed_at, tz) ?? '—'} />
        <DhSpec label="Labour hours" mono value={fmtHours(wo.labor_hours, 1)} />
        <DhSpec label="Parts" mono value={fmtCurrency(wo.parts_cost, currency)} />
        <DhSpec label="Total" mono value={fmtCurrency(wo.total_cost, currency)} />
      </dl>
      {[
        ['Description', wo.description],
        ['Findings', wo.findings],
        ['Resolution', wo.resolution],
      ].map(([label, text]) => (
        <div key={label} className="border-t border-fieldiq-border px-4 py-3">
          <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</p>
          <p className="mt-1 whitespace-pre-line text-sm text-fieldiq-text">{text || '—'}</p>
        </div>
      ))}
      <PartsList wo={wo} currency={currency} />
    </div>
  );
}

export default function WorkOrderDetailScreen() {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const query = useDownholeWorkOrder(workOrderId);
  const failure = queryLoadFailure(query);
  const wo = query.data?.data ?? null;
  const meta = query.data?.meta;
  const tz = meta?.time_zone ?? null;
  const currency = meta?.currency ?? null;
  const canManage = !!wo && canManageDownholeWorkOrders(user?.role) && isLive(wo);

  return (
    <div data-testid="downhole-work-order-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Work order"
        title={wo ? wo.wo_number || 'Work order' : query.isLoading ? 'Loading…' : 'Work order'}
        onBack={back}
        backLabel="Back to work orders"
        actions={wo && canManage ? <WorkOrderActions wo={wo} currency={currency} /> : undefined}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel eyebrow="Shop" title="Findings and costs" testId="downhole-work-order-detail">
            {/* A failed BACKGROUND refetch keeps the order (and any open dialog) and says so. */}
            {wo ? (
              <>
                {failure && (
                  <p role="status" className="border-b border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                    This work order could not be refreshed just now, so it shows what loaded earlier.
                  </p>
                )}
                <WorkOrderDetail wo={wo} tz={tz} currency={currency} />
              </>
            ) : query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading work order" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this work order" />
            ) : (
              <DhEmpty>This work order does not exist.</DhEmpty>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
